import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import {
  getPromptConfigTools,
  updatePromptConfigTool,
} from "../../services/prompt-ai";
import { useAppInfo, useAppInfoFromUralParams } from "./common-hook";

export const SUB_AGENT_TOOL_TYPE = "sub_agent";

/**
 * Lists the sub-agents of the current app.
 *
 * Sub-agents are stored as prompt-config tools of type `sub_agent`, so this
 * reuses the existing tools API rather than introducing a new endpoint.
 */
export function useSubAgentList() {
  const { appId: paramAppId } = useAppInfoFromUralParams();
  const appInfo = useAppInfo();

  const appId = paramAppId || appInfo?.appId;

  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchSubAgents = useCallback(async () => {
    if (!appId) return;
    setLoading(true);
    setError(false);
    try {
      const data = await getPromptConfigTools(appId);
      const all = Array.isArray(data) ? data : [];
      setDataList(all.filter((t) => t.type === SUB_AGENT_TOOL_TYPE));
    } catch (e) {
      console.error("Failed to fetch sub-agents:", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    fetchSubAgents();
  }, [fetchSubAgents]);

  return { dataList, loading, error, refetch: fetchSubAgents, appId, appInfo };
}

/**
 * Resolves the agent scope for the current route.
 *
 * Returns the active sub-agent (when the URL carries `:subAgentId`) together
 * with its `toolData`, plus a `saveToolData` helper that persists a patch while
 * leaving the rest of the tool definition untouched.
 */
export function useAgentScope() {
  const { subAgentId } = useParams();
  const { appId: paramAppId } = useAppInfoFromUralParams();
  const appInfo = useAppInfo();

  const appId = paramAppId || appInfo?.appId;

  const [subAgent, setSubAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchSubAgent = useCallback(async () => {
    if (!appId || !subAgentId) {
      setSubAgent(null);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const data = await getPromptConfigTools(appId);
      const all = Array.isArray(data) ? data : [];
      const found = all.find(
        (t) => t.type === SUB_AGENT_TOOL_TYPE && String(t.id) === String(subAgentId)
      );
      setSubAgent(found || null);
      if (!found) setError(true);
    } catch (e) {
      console.error("Failed to fetch sub-agent:", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [appId, subAgentId]);

  useEffect(() => {
    fetchSubAgent();
  }, [fetchSubAgent]);

  const toolData = useMemo(() => subAgent?.toolData || {}, [subAgent]);

  /**
   * Merge a patch into the sub-agent's toolData and persist it.
   * Re-sends the full tool definition, since the API expects a whole object.
   */
  const saveToolData = useCallback(
    async (patch) => {
      if (!appId || !subAgent) return { ok: false };
      const nextToolData = { ...toolData, ...patch };
      const result = await updatePromptConfigTool(appId, subAgent.id, {
        name: subAgent.name,
        type: SUB_AGENT_TOOL_TYPE,
        description: subAgent.description || "",
        parameters: subAgent.parameters || {},
        toolData: nextToolData,
      });
      const ok = result?.status === 200 || result?.status === 201;
      if (ok) setSubAgent((prev) => ({ ...prev, toolData: nextToolData }));
      return { ok };
    },
    [appId, subAgent, toolData]
  );

  /**
   * Merge a patch into the sub-agent's top-level fields (name, description,
   * parameters) and persist it, leaving `toolData` untouched.
   */
  const saveMeta = useCallback(
    async (patch) => {
      if (!appId || !subAgent) return { ok: false };
      const next = {
        name: subAgent.name,
        type: SUB_AGENT_TOOL_TYPE,
        description: subAgent.description || "",
        parameters: subAgent.parameters || {},
        ...patch,
        toolData,
      };
      const result = await updatePromptConfigTool(appId, subAgent.id, next);
      const ok = result?.status === 200 || result?.status === 201;
      if (ok) setSubAgent((prev) => ({ ...prev, ...patch }));
      return { ok };
    },
    [appId, subAgent, toolData]
  );

  return {
    appId,
    appInfo,
    subAgentId,
    subAgent,
    toolData,
    saveToolData,
    saveMeta,
    isSubAgentScope: !!subAgentId,
    loading,
    error,
    refetch: fetchSubAgent,
  };
}
